import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  Users, Briefcase, Activity, Sparkles, Key, Eye, EyeOff,
  Search, ChevronLeft, ChevronRight, Building2, Plus, Edit2,
  Trash2, X, Save, CheckCircle, ClipboardList, ChevronDown, UserPlus,
  ArrowUpRight, TrendingUp, Zap, User, Shield, ExternalLink, RefreshCw, MoreVertical,
  Check, AlertCircle, Mail, AtSign, Briefcase as RoleIcon
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// ─── Tab definitions ──────────────────────────────────────────────────────────
const getTabs = (t) => [
  { id: 'overview', label: t('tab_overview'), icon: Activity },
  { id: 'personnel', label: t('tab_personnel'), icon: Key },
  { id: 'companies', label: t('tab_companies'), icon: Building2 },
  { id: 'applications', label: t('tab_applications'), icon: ClipboardList },
];

// ─── Company Form Modal ───────────────────────────────────────────────────────
function CompanyModal({ company, onClose, onSave }) {
  const { t } = useLanguage();
  const isEdit = !!company?.id;
  const [form, setForm] = useState({
    name: company?.name || '',
    description: company?.description || '',
    website: company?.website || '',
    industry: company?.industry || '',
    size: company?.size || '',
    location: company?.location || '',
    logo_url: company?.logo_url || '',
    is_verified: company?.is_verified || false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isEdit) {
        const { data } = await api.put(`/companies/${company.id}`, form);
        onSave(data, 'edit');
      } else {
        const { data } = await api.post('/companies', form);
        onSave(data, 'create');
      }
    } catch (err) {
      setError(err.response?.data?.error || t('failed_save_post'));
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.2)',
    border: '1px solid var(--glass-border)', borderRadius: '12px',
    color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit'
  };
  const labelStyle = {
    fontSize: '0.7rem', fontWeight: 800, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem',
    display: 'flex', alignItems: 'center', gap: '0.5rem'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem'
    }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '800px', padding: 0, position: 'relative', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ padding: '0.5rem', borderRadius: '10px', background: 'var(--accent-primary)15', color: 'var(--accent-primary)' }}>
              <Building2 size={20} />
            </div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.02em' }}>
              {isEdit ? t('edit_company') : t('new_company')}
            </h3>
          </div>
          <button onClick={onClose} className="hover-bright" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.5rem' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '2rem', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: '2rem' }} className="grid-to-stack">
          {/* Form Side */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {error && (
              <div style={{ background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.2)', color: '#f472b6', padding: '0.875rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600 }}>
                {error}
              </div>
            )}

            <div>
              <label style={labelStyle}><AtSign size={14} opacity={0.6} /> {t('company_name_label')}</label>
              <input style={inputStyle} required value={form.name} onChange={set('name')} placeholder={t('example_company_name')} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={labelStyle}><Activity size={14} opacity={0.6} /> {t('industry_label')}</label>
                <input style={inputStyle} value={form.industry} onChange={set('industry')} placeholder={t('example_industry')} />
              </div>
              <div>
                <label style={labelStyle}><Users size={14} opacity={0.6} /> {t('size_label_admin')}</label>
                <input style={inputStyle} value={form.size} onChange={set('size')} placeholder={t('example_size')} />
              </div>
            </div>

            <div>
              <label style={labelStyle}><Building2 size={14} opacity={0.6} /> {t('location_label_admin')}</label>
              <input style={inputStyle} value={form.location} onChange={set('location')} placeholder={t('example_location')} />
            </div>

            <div>
              <label style={labelStyle}><ExternalLink size={14} opacity={0.6} /> {t('logo_url_label')}</label>
              <input style={inputStyle} value={form.logo_url} onChange={set('logo_url')} placeholder="https://path-to-logo.png" />
            </div>

            <div>
              <label style={labelStyle}><ClipboardList size={14} opacity={0.6} /> {t('description_label_admin')}</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }} value={form.description} onChange={set('description')} placeholder={t('short_description_placeholder')} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', borderRadius: '12px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)' }}>
              <input type="checkbox" id="v_check" checked={form.is_verified} onChange={set('is_verified')} style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }} />
              <label htmlFor="v_check" style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-heading)', cursor: 'pointer' }}>{t('verified_label')}</label>
            </div>

            <button type="submit" disabled={loading} className="btn-cyber" style={{ padding: '1rem', marginTop: '1rem', width: '100%', justifyContent: 'center' }}>
              {loading ? <RefreshCw size={18} className="animate-spin" /> : (isEdit ? t('save_changes') : t('create_company_btn'))}
            </button>
          </form>

          {/* Preview Side */}
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Eye size={14} /> {t('preview_label')}
            </div>

            <div className="glass-card" style={{ padding: '1.25rem', border: '1px solid var(--accent-primary)30', background: 'rgba(99,102,241,0.05)', position: 'sticky', top: 0 }}>
              {/* Visual Preview mirroring the main cards */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '14px',
                  background: 'linear-gradient(135deg, var(--bg-surface), var(--bg-card))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.25rem', fontWeight: 900, color: 'var(--accent-primary)',
                  border: '1px solid var(--glass-border)', padding: '4px'
                }}>
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (form.name ? form.name[0] : '?')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {form.name || 'Company Name'}
                    </div>
                    {form.is_verified && <CheckCircle size={14} color="#4ade80" />}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    {form.industry || 'Industry'} • {form.location || 'Location'}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: '1.5', height: '60px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                {form.description || 'No description provided yet. Add one to help candidates understand your mission.'}
              </div>

              <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--accent-primary)', textTransform: 'uppercase' }}>0 {t('open_jobs_label_admin')}</div>
                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)' }} />
              </div>
            </div>

            <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '14px', background: 'rgba(255,255,255,0.02)', border: '1px dashed var(--glass-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 600 }}>
                <Shield size={14} />
                <span>{t('admin_visibility_notice')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Job Form Modal ───────────────────────────────────────────────────────────
function JobModal({ job, companies, onClose, onSave }) {
  const { t } = useLanguage();
  const isEdit = !!job?.id;
  const [form, setForm] = useState({
    company_id: job?.company_id || (companies[0]?.id || ''),
    title: job?.title || '',
    description: job?.description || '',
    required_skills: (job?.required_skills || []).join(', '),
    experience_years: job?.experience_years || '',
    location: job?.location || '',
    salary_range: job?.salary_range || '',
    type: job?.type || 'full-time',
    status: job?.status || 'open',
    success_fee: job?.success_fee || '',
    deadline: job?.deadline ? job.deadline.slice(0, 10) : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const payload = {
      ...form,
      required_skills: form.required_skills.split(',').map(s => s.trim()).filter(Boolean),
      experience_years: form.experience_years ? parseInt(form.experience_years) : null,
      success_fee: form.success_fee ? parseFloat(form.success_fee) : null,
      deadline: form.deadline || null,
    };
    try {
      if (isEdit) {
        const { data } = await api.put(`/jobs/${job.id}`, payload);
        onSave(data, 'edit');
      } else {
        const { data } = await api.post('/jobs', payload);
        onSave(data, 'create');
      }
    } catch (err) {
      setError(err.response?.data?.error || t('failed_save_post'));
    }
    setLoading(false);
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.75rem 1rem', background: 'var(--bg-deep)', border: '1px solid var(--glass-border)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none' };
  const labelStyle = { fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem', display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '620px', padding: '2.5rem', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.4rem' }}>
          <X size={18} />
        </button>
        <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-heading)', marginBottom: '2rem' }}>
          {isEdit ? t('edit_job_title') : t('new_job_title')}
        </h3>
        {error && <div style={{ background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.2)', color: '#f472b6', padding: '0.875rem', borderRadius: '10px', marginBottom: '1.25rem', fontSize: '0.85rem', fontWeight: 600 }}>{error}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          <div>
            <label style={labelStyle}>{t('company_label_admin')}</label>
            <select style={inputStyle} required value={form.company_id} onChange={set('company_id')}>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>{t('position_title_label')}</label><input style={inputStyle} required value={form.title} onChange={set('title')} placeholder={t('example_job_title')} /></div>
          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>{t('job_type_label')}</label>
              <select style={inputStyle} value={form.type} onChange={set('type')}>
                <option value="full-time">{t('type_full_time')}</option>
                <option value="part-time">{t('type_part_time')}</option>
                <option value="contract">{t('type_contract')}</option>
                <option value="internship">{t('type_internship')}</option>
                <option value="remote">{t('type_remote')}</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('job_status_label')}</label>
              <select style={inputStyle} value={form.status} onChange={set('status')}>
                <option value="open">{t('status_open')}</option>
                <option value="closed">{t('status_closed')}</option>
                <option value="draft">{t('status_draft')}</option>
              </select>
            </div>
          </div>
          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div><label style={labelStyle}>{t('location_label_admin')}</label><input style={inputStyle} value={form.location} onChange={set('location')} placeholder={t('example_location_remote')} /></div>
            <div><label style={labelStyle}>{t('salary_range_label')}</label><input style={inputStyle} value={form.salary_range} onChange={set('salary_range')} placeholder={t('example_salary')} /></div>
          </div>
          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div><label style={labelStyle}>{t('experience_years_label')}</label><input style={inputStyle} type="number" min="0" value={form.experience_years} onChange={set('experience_years')} placeholder="2" /></div>
            <div><label style={labelStyle}>{t('success_fee_label')}</label><input style={inputStyle} type="number" min="0" step="0.01" value={form.success_fee} onChange={set('success_fee')} placeholder="500" /></div>
          </div>
          <div><label style={labelStyle}>{t('required_skills_label_admin')}</label>
            <input style={inputStyle} value={form.required_skills} onChange={set('required_skills')} placeholder="React, Node.js, PostgreSQL" />
          </div>
          <div><label style={labelStyle}>{t('deadline_label_admin')}</label><input style={inputStyle} type="date" value={form.deadline} onChange={set('deadline')} /></div>
          <div><label style={labelStyle}>{t('description_label_admin')} *</label>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '100px', fontFamily: 'inherit' }} required value={form.description} onChange={set('description')} placeholder={t('detailed_description_placeholder')} />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
            <button type="button" onClick={onClose} disabled={loading} className="btn-cyber" style={{ flex: 1, padding: '0.875rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', color: 'var(--text-body)' }}>{t('cancel_btn_admin')}</button>
            <button type="submit" disabled={loading} className="btn-cyber" style={{ flex: 2, padding: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', opacity: loading ? 0.7 : 1 }}>
              <Save size={16} /> {loading ? t('saving_btn_admin') : (isEdit ? t('save_btn_admin') : t('create_btn_admin'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
const getStatusMeta = (t) => ({
  pending: { bg: 'rgba(99,102,241,0.12)', color: '#818cf8', label: t('status_pending') },
  shortlisted: { bg: 'rgba(34,211,238,0.12)', color: '#22d3ee', label: t('status_shortlisted') },
  hired: { bg: 'rgba(34,197,94,0.12)', color: '#4ade80', label: t('status_hired') },
  rejected: { bg: 'rgba(244,114,182,0.1)', color: '#f472b6', label: t('status_rejected') },
});

function StatusBadge({ status }) {
  const { t } = useLanguage();
  const STATUS_META = getStatusMeta(t);
  const m = STATUS_META[status] || { bg: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)', label: status };
  return (
    <span style={{ padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
}

// ─── Invite User Modal ────────────────────────────────────────────────────────
function InviteModal({ isOpen, form, onFormChange, onSubmit, onClose, companies = [] }) {
  const { t } = useLanguage();
  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '400px', padding: '2.5rem', position: 'relative' }}>
        <h2 style={{ fontSize: '1.5rem', color: 'var(--text-heading)', marginBottom: '1.5rem' }}>{t('invite_user_title')}</h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '0.4rem' }}>{t('email_address_label_admin')}</label>
            <input
              type="email"
              placeholder="user@example.com"
              value={form.email}
              onChange={(e) => onFormChange({ ...form, email: e.target.value })}
              className="cyber-input"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '0.4rem' }}>{t('role_label_admin')}</label>
            <select
              value={form.role}
              onChange={(e) => onFormChange({ ...form, role: e.target.value, companyId: '' })}
              className="cyber-input"
              style={{ width: '100%' }}
            >
              <option value="member">{t('role_ap_member')}</option>
              <option value="volunteer">{t('role_volunteer')}</option>
              <option value="ap_admin">{t('role_ap_admin')}</option>
              <option value="guest">{t('role_guest')}</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '0.4rem' }}>Company Admin (Optional)</label>
            <select
              value={form.companyId}
              onChange={(e) => onFormChange({ ...form, companyId: e.target.value, role: e.target.value ? 'member' : form.role })}
              className="cyber-input"
              style={{ width: '100%' }}
            >
              <option value="">— None (not a company admin)</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {form.companyId && (
              <p style={{ fontSize: '0.7rem', color: 'var(--accent-cyan)', marginTop: '0.3rem' }}>
                User will be invited as Member + Company Admin for this company.
              </p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button onClick={onClose} className="btn-cyber" style={{ flex: 1, padding: '0.875rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', color: 'var(--text-body)' }}>
            {t('cancel_btn_admin')}
          </button>
          <button onClick={onSubmit} className="btn-cyber" style={{ flex: 1, padding: '0.875rem', background: 'var(--accent-primary)' }}>
            {t('send_invite_btn')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit User Modal ──────────────────────────────────────────────────────────
function EditUserModal({ user, departments, onClose, onSave }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    username: user?.username || '',
    role: user?.role || 'member',
    department_id: departments.find(d => d.name === user?.department)?.id || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.patch(`/admin/users/${user.id}`, form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || t('update_failed'));
    }
    setLoading(false);
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.8rem 1rem', background: 'var(--bg-deep)', border: '1px solid var(--glass-border)', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none', transition: 'border-color 0.2s' };
  const labelStyle = { fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '520px', padding: '2.5rem', position: 'relative', border: '1px solid rgba(255,255,255,0.1)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '10px', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.5rem', display: 'flex' }}><X size={18} /></button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'var(--accent-primary)15', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--accent-primary)30' }}>
            <Edit2 size={24} color="var(--accent-primary)" />
          </div>
          <div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.02em' }}>{t('edit_user_title')}</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>ID: {user.id}</p>
          </div>
        </div>

        {error && <div style={{ background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.2)', color: '#f472b6', padding: '1rem', borderRadius: '12px', marginBottom: '1.5rem', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><AlertCircle size={16} /> {error}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div><label style={labelStyle}>{t('table_name')}</label><input style={inputStyle} value={form.full_name} onChange={set('full_name')} required /></div>

          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
            <div><label style={labelStyle}>{t('table_email')}</label><input type="email" style={inputStyle} value={form.email} onChange={set('email')} required /></div>
            <div><label style={labelStyle}>{t('table_username')}</label><input style={inputStyle} value={form.username} onChange={set('username')} required /></div>
          </div>

          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
            <div>
              <label style={labelStyle}>{t('table_department')}</label>
              <select style={inputStyle} value={form.department_id} onChange={set('department_id')}>
                <option value="">{t('all_departments')}</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('table_role')}</label>
              <select style={inputStyle} value={form.role} onChange={set('role')} required>
                <option value="member">{t('role_ap_member')}</option>
                <option value="volunteer">{t('role_volunteer')}</option>
                <option value="ap_admin">{t('role_ap_admin')}</option>
                <option value="guest">{t('role_guest')}</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="button" onClick={onClose} disabled={loading} className="btn-cyber" style={{ flex: 1, padding: '0.9rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', color: 'var(--text-body)' }}>{t('cancel_btn_admin')}</button>
            <button type="submit" disabled={loading} className="btn-cyber" style={{ flex: 2, padding: '0.9rem', background: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', opacity: loading ? 0.7 : 1 }}>
              {loading ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
              {loading ? t('saving_btn_admin') : t('save_changes_btn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Admin Component ─────────────────────────────────────────────────────
function Admin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const TABS = getTabs(t);

  // ── Overview state ──
  const [stats, setStats] = useState({ users: 0, posts: 0, opportunities: 0, totalCompanies: 0, openJobs: 0, pendingApps: 0 });
  const [celebrations, setCelebrations] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Personnel state ──
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(''); // Filter users by role
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [usersLoading, setUsersLoading] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member', companyId: '' });
  const USERS_PER_PAGE = 10;

  const [departments, setDepartments] = useState([]);
  const [isEditUserModalOpen, setEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // ── Companies state ──
  const [companies, setCompanies] = useState([]);
  const [companyModal, setCompanyModal] = useState(null); // null | {} | {id,...}
  const [jobModal, setJobModal] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [companyJobs, setCompanyJobs] = useState({}); // companyId → jobs[]
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // ── Applications state ──
  const [applications, setApplications] = useState([]);
  const [appStatusFilter, setAppStatusFilter] = useState('all');
  const [appsLoading, setAppsLoading] = useState(false);
  const [updatingApp, setUpdatingApp] = useState(null);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchStats(), fetchCelebrations(), fetchAllUsers(1, '')]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (activeTab === 'companies') fetchCompanies();
    if (activeTab === 'applications') fetchApplications();
  }, [activeTab]);

  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); fetchAllUsers(1, searchQuery, roleFilter); }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, roleFilter]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchStats = async () => {
    try {
      const [usersRes, postsRes, oppsRes] = await Promise.allSettled([
        api.get('/users'), api.get('/posts'), api.get('/opportunities')
      ]);
      // Also try admin stats for companies/jobs
      let adminStats = {};
      try { const { data } = await api.get('/admin/stats'); adminStats = data; } catch { }

      setStats({
        users: usersRes.status === 'fulfilled' && Array.isArray(usersRes.value?.data) ? usersRes.value.data.length : 0,
        posts: postsRes.status === 'fulfilled' && Array.isArray(postsRes.value?.data) ? postsRes.value.data.length : 0,
        opportunities: oppsRes.status === 'fulfilled' && Array.isArray(oppsRes.value?.data) ? oppsRes.value.data.length : 0,
        totalCompanies: adminStats.totalCompanies || 0,
        openJobs: adminStats.openJobs || 0,
        pendingApps: adminStats.pendingApps || 0,
      });
    } catch { }
  };

  const fetchCelebrations = async () => {
    try {
      const { data } = await api.get('/users/celebrations/today');
      setCelebrations(Array.isArray(data) ? data : []);
    } catch { setCelebrations([]); }
  };

  const fetchAllUsers = async (page = 1, search = '', role = '') => {
    setUsersLoading(true);
    try {
      const { data } = await api.get(`/admin/users/all?page=${page}&limit=${USERS_PER_PAGE}&search=${encodeURIComponent(search)}&role=${role}`);
      if (data && Array.isArray(data.users)) {
        setAllUsers(data.users);
        setPagination(data.pagination || { total: 0, totalPages: 1 });
      } else {
        setAllUsers(Array.isArray(data) ? data : []);
        setPagination({ total: 0, totalPages: 1 });
      }
    } catch { setAllUsers([]); setPagination({ total: 0, totalPages: 1 }); }
    setUsersLoading(false);
  };

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const { data } = await api.get('/companies');
      setCompanies(Array.isArray(data) ? data : []);
    } catch { setCompanies([]); }
    setCompaniesLoading(false);
  };

  const fetchCompanyJobs = async (companyId) => {
    try {
      const { data } = await api.get(`/jobs?company_id=${companyId}&status=all`);
      setCompanyJobs(prev => ({ ...prev, [companyId]: Array.isArray(data) ? data : [] }));
    } catch { setCompanyJobs(prev => ({ ...prev, [companyId]: [] })); }
  };

  const fetchApplications = async () => {
    setAppsLoading(true);
    try {
      const params = appStatusFilter !== 'all' ? `?status=${appStatusFilter}` : '';
      const { data } = await api.get(`/admin/applications${params}`);
      setApplications(Array.isArray(data) ? data : []);
    } catch { setApplications([]); }
    setAppsLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'applications') fetchApplications();
  }, [appStatusFilter]);

  const fetchDepartments = useCallback(async () => {
    try {
      const { data } = await api.get('/departments');
      setDepartments(data || []);
    } catch (err) { console.error('Failed to fetch departments:', err); }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePageChange = (p) => {
    setCurrentPage(p);
    fetchAllUsers(p, searchQuery, roleFilter);
  };

  const handleCompanySave = (saved, mode) => {
    if (mode === 'create') setCompanies(prev => [...prev, { ...saved, job_count: 0 }]);
    else setCompanies(prev => prev.map(c => c.id === saved.id ? { ...c, ...saved } : c));
    setCompanyModal(null);
    fetchStats(); // Synchronize overview counters
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm(t('delete_company_confirm'))) return;
    try {
      await api.delete(`/companies/${id}`);
      setCompanies(prev => prev.filter(c => c.id !== id));
      if (expandedCompany === id) setExpandedCompany(null);
      fetchStats(); // Synchronize overview counters
    } catch (err) {
      alert(err.response?.data?.error || t('failed_save_post'));
    }
  };

  const handleJobSave = (saved, mode) => {
    const cid = saved.company_id;
    if (mode === 'create') {
      setCompanyJobs(prev => ({ ...prev, [cid]: [...(prev[cid] || []), saved] }));
      setCompanies(prev => prev.map(c => c.id === cid ? { ...c, job_count: (c.job_count || 0) + 1 } : c));
    } else {
      setCompanyJobs(prev => ({
        ...prev,
        [cid]: (prev[cid] || []).map(j => j.id === saved.id ? saved : j)
      }));
    }
    setJobModal(null);
    fetchStats(); // Synchronize overview counters (Open Jobs)
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(t('delete_job_confirm_admin'))) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      const cid = job.company_id;
      setCompanyJobs(prev => ({ ...prev, [cid]: (prev[cid] || []).filter(j => j.id !== job.id) }));
      setCompanies(prev => prev.map(c => c.id === cid ? { ...c, job_count: Math.max((c.job_count || 1) - 1, 0) } : c));
      fetchStats(); // Synchronize overview counters (Open Jobs)
    } catch (err) { alert(err.response?.data?.error || t('failed_save_post')); }
  };

  const handleToggleCompany = (id) => {
    if (expandedCompany === id) { setExpandedCompany(null); return; }
    setExpandedCompany(id);
    if (!companyJobs[id]) fetchCompanyJobs(id);
  };

  const handleUpdateAppStatus = async (appId, status) => {
    setUpdatingApp(appId);
    try {
      await api.patch(`/jobs/applications/${appId}`, { status });
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
      fetchStats(); // Synchronize overview counters (Pending Apps)
    } catch (err) { alert(err.response?.data?.error || t('failed_save_post')); }
    setUpdatingApp(null);
  };

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.role) {
      alert(t('fill_all_fields'));
      return;
    }
    try {
      const payload = {
        email: inviteForm.email,
        role: inviteForm.role,
        company_id: inviteForm.companyId || null
      };
      await api.post('/auth/invites', payload);
      alert(t('invite_sent_success').replace('{email}', inviteForm.email));
      setInviteForm({ email: '', role: 'member', companyId: '' });
      setInviteModal(false);
      await fetchAllUsers(1, searchQuery);
    } catch (err) {
      alert(t('update_failed') + (err.response?.data?.error || err.message));
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    await fetchAllUsers(currentPage, searchQuery);
    setEditUserModalOpen(false);
    setEditingUser(null);
    alert(t('user_updated_success'));
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!window.confirm(t('confirm_toggle_status'))) return;
    setIsUpdatingStatus(true);
    try {
      await api.patch(`/admin/users/${userId}/status`, { is_active: !currentStatus });
      await fetchAllUsers(currentPage, searchQuery);
    } catch (err) {
      alert(t('update_failed') + (err.response?.data?.error || err.message));
    }
    setIsUpdatingStatus(false);
  };

  if (loading) {
    return (
      <div className="container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div style={{ color: 'var(--text-secondary)' }}>{t('loading_admin')}</div>
      </div>
    );
  }

  const statCards = [
    { label: t('active_personnel'), value: stats.users, icon: Users, color: '#818cf8', gradient: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.08) 100%)', border: 'rgba(99,102,241,0.25)', action: () => setActiveTab('personnel') },
    { label: t('network_posts_stat'), value: stats.posts, icon: Activity, color: '#f472b6', gradient: 'linear-gradient(135deg, rgba(244,114,182,0.12) 0%, rgba(236,72,153,0.06) 100%)', border: 'rgba(244,114,182,0.2)', action: () => navigate('/') },
    { label: t('opportunities_stat'), value: stats.opportunities, icon: Briefcase, color: '#4ade80', gradient: 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(16,185,129,0.06) 100%)', border: 'rgba(34,197,94,0.2)', action: () => navigate('/opportunities', { state: { activeTab: 'opportunities' } }) },
    { label: t('tab_companies'), value: stats.totalCompanies, icon: Building2, color: '#22d3ee', gradient: 'linear-gradient(135deg, rgba(34,211,238,0.12) 0%, rgba(6,182,212,0.06) 100%)', border: 'rgba(34,211,238,0.2)', action: () => setActiveTab('companies') },
    { label: t('open_jobs_stat'), value: stats.openJobs, icon: Briefcase, color: '#facc15', gradient: 'linear-gradient(135deg, rgba(250,204,21,0.1) 0%, rgba(234,179,8,0.05) 100%)', border: 'rgba(250,204,21,0.2)', action: () => navigate('/opportunities', { state: { activeTab: 'jobs' } }) },
    { label: t('pending_apps_stat'), value: stats.pendingApps, icon: ClipboardList, color: '#c084fc', gradient: 'linear-gradient(135deg, rgba(192,132,252,0.12) 0%, rgba(168,85,247,0.06) 100%)', border: 'rgba(192,132,252,0.2)', action: () => setActiveTab('applications') },
  ];

  return (
    <div className="container">
      <style>{`
        .admin-stat-card {
          position: relative;
          padding: 1.75rem;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }
        .admin-stat-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 20px;
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        .admin-stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px -12px rgba(0,0,0,0.4);
        }
        .admin-stat-card:hover .stat-arrow {
          opacity: 1;
          transform: translate(0, 0);
        }
        .admin-stat-card:hover .stat-icon-wrap {
          transform: scale(1.1);
        }
        .admin-stat-card:active {
          transform: translateY(-2px);
        }
        .stat-arrow {
          opacity: 0;
          transform: translate(-4px, 4px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .stat-icon-wrap {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .admin-tab-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 1.4rem;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          font-size: 0.88rem;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        .admin-tab-btn:hover {
          background: rgba(99,102,241,0.08) !important;
          color: var(--text-heading) !important;
        }
        .admin-welcome-card {
          position: relative;
          border-radius: 24px;
          padding: 2.5rem;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.04) 50%, rgba(34,211,238,0.06) 100%);
          border: 1px solid rgba(99,102,241,0.15);
        }
        .admin-welcome-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%);
          pointer-events: none;
        }
        .admin-welcome-card::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -10%;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(34,211,238,0.06) 0%, transparent 70%);
          pointer-events: none;
        }
        .admin-quick-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.25rem;
          border-radius: 12px;
          border: 1px solid var(--glass-border);
          background: rgba(255,255,255,0.03);
          color: var(--text-body);
          font-size: 0.82rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .admin-quick-btn:hover {
          background: rgba(99,102,241,0.1);
          border-color: rgba(99,102,241,0.3);
          color: var(--text-heading);
          transform: translateY(-2px);
        }
        @media (max-width: 768px) {
          .admin-stat-card { padding: 1.25rem; }
          .admin-welcome-card { padding: 1.5rem; }
        }
      `}</style>

      {/* Title */}
      <div style={{ marginBottom: '2.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', justifyContent: 'center' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '12px',
            background: 'linear-gradient(135deg, var(--accent-primary), rgba(139,92,246,0.8))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px -6px rgba(99,102,241,0.4)'
          }}>
            <Zap size={22} color="white" />
          </div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            {t('command_center_title').split(' ')[0]} <span style={{
              background: 'linear-gradient(90deg, var(--accent-primary), #22d3ee)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>{t('command_center_title').split(' ')[1]}</span>
          </h2>
        </div>
        <p style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.95rem', textAlign: 'center' }}>{t('command_center_desc')}</p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: '3rem',
        justifyContent: 'center',
        marginBottom: '2.5rem',
        borderBottom: '1px solid var(--glass-border)',
      }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.85rem 1.5rem',
                border: 'none', cursor: 'pointer',
                background: 'transparent',
                color: isActive ? 'var(--accent-primary)' : 'var(--text-muted)',
                fontWeight: isActive ? 800 : 500,
                fontSize: '0.9rem',
                transition: 'all 0.25s ease',
                position: 'relative',
                borderBottom: isActive ? '2px solid var(--accent-primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--text-body)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              <tab.icon size={16} style={{ opacity: isActive ? 1 : 0.5 }} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── OVERVIEW TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <>
          {/* Stat Cards Grid */}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1.25rem'
          }}
            className="grid-to-stack"
          >
            {statCards.map(s => (
              <div
                key={s.label}
                className="admin-stat-card"
                onClick={s.action}
                style={{
                  background: s.gradient,
                  border: `1px solid ${s.border}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                  <div className="stat-icon-wrap" style={{
                    width: '44px', height: '44px', borderRadius: '14px',
                    background: `${s.color}15`,
                    border: `1px solid ${s.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <s.icon size={22} color={s.color} />
                  </div>
                  <div className="stat-arrow">
                    <ArrowUpRight size={20} color={s.color} />
                  </div>
                </div>
                <div style={{
                  fontSize: '2.75rem', fontWeight: 900, color: 'var(--text-heading)',
                  letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '0.5rem'
                }}>
                  {s.value}
                </div>
                <div style={{
                  color: 'var(--text-muted)', fontSize: '0.78rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.08em'
                }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </>
      )}


      {/* ── PERSONNEL TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'personnel' && (
        <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Header Actions */}
          <div className="glass-card" style={{ padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ padding: '0.75rem', borderRadius: '12px', background: 'var(--accent-primary)10', border: '1px solid var(--accent-primary)20' }}>
                <Users size={24} color="var(--accent-primary)" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.02em', marginBottom: '0.15rem' }}>{t('platform_users')}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80' }} />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>{pagination.total} {t('total_stat')}</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', minWidth: '320px' }}>
                <Search size={16} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input type="text" placeholder={t('search_users_placeholder')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '0.65rem 1rem 0.65rem 2.75rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }} style={{ padding: '0 1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', borderRadius: '10px', color: 'var(--text-body)', fontSize: '0.82rem', fontWeight: 600, outline: 'none', cursor: 'pointer' }}>
                <option value="">{t('all_roles_filter')}</option>
                <option value="member">{t('role_ap_member')}</option>
                <option value="volunteer">{t('role_volunteer')}</option>
                <option value="ap_admin">{t('role_ap_admin')}</option>
                <option value="guest">{t('role_guest')}</option>
              </select>
              <button onClick={() => setInviteModal(true)} className="btn-cyber" style={{ background: 'var(--accent-primary)', padding: '0.6rem 1.25rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <UserPlus size={16} /> {t('invite_user_title')}
              </button>
            </div>
          </div>

          {/* Table Container */}
          <div className="glass-card" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', opacity: usersLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--glass-border)' }}>
                    <th style={{ textAlign: 'left', padding: '1.25rem 1.5rem 1.25rem calc(1.5rem + 40px + 1rem)', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>{t('table_name')}</th>
                    <th style={{ textAlign: 'left', padding: '1.25rem 1rem 1.25rem calc(1rem + 12px + 0.4rem)', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>{t('table_email')}</th>
                    <th style={{ textAlign: 'center', padding: '1.25rem 1rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>{t('table_department')}</th>
                    <th style={{ textAlign: 'center', padding: '1.25rem 1rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em', width: '1%', whiteSpace: 'nowrap' }}>{t('table_role')}</th>
                    <th style={{ textAlign: 'center', padding: '1.25rem 1rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em', width: '1%', whiteSpace: 'nowrap' }}>{t('table_status')}</th>
                    <th style={{ textAlign: 'center', padding: '1.25rem 1.5rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em', width: '1%', whiteSpace: 'nowrap' }}>{t('table_action')}</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '0.88rem' }}>
                  {allUsers.map((u, i) => {
                    const initials = u.full_name ? u.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : u.username[0].toUpperCase();
                    const active = u.is_active === 1;

                    return (
                      <tr key={u.id || i} className="admin-table-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.2s' }}>
                        <td style={{ padding: '1rem 1.50rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: `linear-gradient(135deg, var(--accent-primary)20 0%, var(--accent-primary)10 100%)`, border: '1px solid var(--accent-primary)20', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)', fontWeight: 800, fontSize: '0.85rem' }}>
                              {initials}
                            </div>
                            <div>
                              <div style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '0.92rem' }}>{u.full_name || 'Anonymous User'}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', fontWeight: 600 }}>@{u.username}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-body)', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                            <Mail size={12} opacity={0.5} /> {u.email}
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1rem', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                            <Building2 size={12} /> {u.department || 'N/A'}
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1rem', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                            padding: '0.35rem 0.85rem', borderRadius: '999px',
                            background: u.role === 'ap_admin' ? 'rgba(244,114,182,0.1)' : 'rgba(139,92,246,0.1)',
                            color: u.role === 'ap_admin' ? 'var(--accent-magenta)' : 'var(--accent-violet)',
                            fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                            whiteSpace: 'nowrap'
                          }}>
                            {u.role === 'ap_admin' ? <Shield size={10} /> : <User size={10} />}
                            {t(`role_${u.role}`)}
                          </span>
                        </td>
                        <td style={{ padding: '1rem 1rem', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                            padding: '0.3rem 0.65rem', borderRadius: '999px',
                            background: active ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.05)',
                            color: active ? '#4ade80' : 'var(--text-muted)',
                            fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase'
                          }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                            {active ? t('status_active') : t('status_inactive')}
                          </span>
                        </td>
                        <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button onClick={() => handleEditUser(u)} className="action-btn" title={t('edit_user_title')} style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', padding: '0.5rem', borderRadius: '8px', color: 'var(--accent-primary)', cursor: 'pointer', display: 'flex' }}>
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleToggleUserStatus(u.id, active)} className="action-btn" title={t('account_status_label')} style={{ background: active ? 'rgba(244,114,182,0.08)' : 'rgba(34,197,94,0.08)', border: active ? '1px solid rgba(244,114,182,0.15)' : '1px solid rgba(34,197,94,0.15)', padding: '0.5rem', borderRadius: '8px', color: active ? '#f472b6' : '#4ade80', cursor: 'pointer', display: 'flex' }}>
                              {active ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {allUsers.length === 0 && !usersLoading && (
                <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Users size={48} style={{ opacity: 0.1, marginBottom: '1rem' }} />
                  <div style={{ fontWeight: 600 }}>{searchQuery ? t('no_users_match') : t('no_personnel_data')}</div>
                </div>
              )}
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1} className="btn-cyber" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', padding: '0.5rem 1rem', fontSize: '0.82rem', opacity: currentPage <= 1 ? 0.3 : 1, cursor: currentPage <= 1 ? 'not-allowed' : 'pointer' }}>
                <ChevronLeft size={16} />
              </button>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>{t('page_label').replace('{current}', currentPage).replace('{total}', pagination.totalPages)}</span>
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= pagination.totalPages} className="btn-cyber" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', padding: '0.5rem 1rem', fontSize: '0.82rem', opacity: currentPage >= pagination.totalPages ? 0.3 : 1, cursor: currentPage >= pagination.totalPages ? 'not-allowed' : 'pointer' }}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* User Modals */}
          {isEditUserModalOpen && <EditUserModal user={editingUser} departments={departments} onClose={() => setEditUserModalOpen(false)} onSave={handleSaveUser} />}
        </div>
      )}
      {/* ── COMPANIES TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'companies' && (
        <div className="animate-fade">
          <div className="glass-card" style={{ padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ padding: '0.75rem', borderRadius: '12px', background: 'var(--accent-primary)10', border: '1px solid var(--accent-primary)20' }}>
                <Building2 size={24} color="var(--accent-primary)" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.02em', marginBottom: '0.15rem' }}>{t('companies_tab_title')}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-primary)' }} />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>{companies.length} {t('companies_registered_label')}</span>
                </div>
              </div>
            </div>
            <button onClick={() => setCompanyModal({})} className="btn-cyber" style={{ padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
              <Plus size={18} /> {t('new_company')}
            </button>
          </div>

          {companiesLoading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '6rem', background: 'rgba(255,255,255,0.02)', borderRadius: '24px', border: '1px dashed var(--glass-border)' }}>
              <div className="loading-spinner" style={{ marginBottom: '1rem' }}></div>
              <p style={{ fontWeight: 600, letterSpacing: '0.05em' }}>{t('loading_label_admin')}</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="glass-card" style={{ padding: '6rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Building2 size={48} style={{ margin: '0 auto 1.5rem', opacity: 0.2 }} />
              <div style={{ fontWeight: 800, fontSize: '1.2rem', color: 'var(--text-heading)', marginBottom: '0.5rem' }}>{t('no_companies_yet')}</div>
              <p style={{ fontSize: '0.9rem', maxWidth: '300px', margin: '0 auto' }}>{t('click_new_company')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {companies.map(company => {
                const isExpanded = expandedCompany === company.id;
                const jobs = companyJobs[company.id] || [];

                return (
                  <div key={company.id} className="glass-card" style={{ padding: 0, overflow: 'hidden', border: isExpanded ? '1px solid var(--accent-primary)40' : '1px solid var(--glass-border)' }}>
                    {/* Background Glow */}
                    {isExpanded && (
                      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '40%', background: 'linear-gradient(to bottom, var(--accent-primary)05, transparent)', pointerEvents: 'none' }} />
                    )}

                    {/* Company Row */}
                    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{
                          width: '56px', height: '56px', borderRadius: '16px',
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1.4rem', fontWeight: 900, color: 'var(--accent-primary)',
                          border: '1px solid var(--glass-border)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                          padding: company.logo_url ? '6px' : '0'
                        }}>
                          {company.logo_url ? (
                            <img src={company.logo_url} alt={company.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : <span style={{ textShadow: '0 0 10px var(--accent-primary)40' }}>{company.name[0]}</span>}
                        </div>
                      </div>

                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                          <h4 style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '1.15rem', letterSpacing: '-0.01em' }}>{company.name}</h4>
                          {company.is_verified && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.65rem', padding: '0.25rem 0.75rem', borderRadius: '8px', background: 'rgba(74,222,128,0.1)', color: '#4ade80', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', border: '1px solid rgba(74,222,128,0.2)' }}>
                              <CheckCircle size={10} /> {t('verified_label')}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                          {[company.industry, company.location].filter(Boolean).map((item, idx) => (
                            <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              {idx > 0 && <span style={{ opacity: 0.3 }}>•</span>}
                              {item}
                            </span>
                          ))}
                          {company.size && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <span style={{ opacity: 0.3 }}>•</span>
                              <Users size={12} style={{ opacity: 0.5 }} /> {company.size}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats & Actions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexShrink: 0 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--text-heading)', lineHeight: 1 }}>{company.job_count || 0}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.2rem' }}>{t('open_jobs_label_admin')}</div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', padding: '0.4rem', borderRadius: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)' }}>
                          <button
                            onClick={() => { setJobModal({ company_id: company.id }); }}
                            title={t('add_job_tooltip')}
                            style={{
                              background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
                              borderRadius: '8px', color: 'var(--accent-primary)', cursor: 'pointer',
                              padding: '0.5rem', display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                            }}
                            className="hover-bright"
                          >
                            <Plus size={16} />
                          </button>
                          <button
                            onClick={() => setCompanyModal(company)}
                            title={t('edit_tooltip')}
                            style={{
                              background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)',
                              borderRadius: '8px', color: 'var(--text-muted)', cursor: 'pointer',
                              padding: '0.5rem', display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                            }}
                            className="hover-bright"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteCompany(company.id)}
                            title={t('delete_tooltip')}
                            style={{
                              background: 'rgba(244,114,182,0.08)', border: '1px solid rgba(244,114,182,0.15)',
                              borderRadius: '8px', color: '#f472b6', cursor: 'pointer',
                              padding: '0.5rem', display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                            }}
                            className="hover-bright"
                          >
                            <Trash2 size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleCompany(company.id)}
                            style={{
                              background: isExpanded ? 'var(--accent-primary)15' : 'rgba(255,255,255,0.05)',
                              border: isExpanded ? '1px solid var(--accent-primary)30' : '1px solid var(--glass-border)',
                              borderRadius: '8px', color: isExpanded ? 'var(--accent-primary)' : 'var(--text-muted)',
                              cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: isExpanded ? 'rotate(180deg)' : 'none'
                            }}
                          >
                            <ChevronDown size={16} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Jobs */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid var(--glass-border)', padding: '1.5rem', background: 'rgba(0,0,0,0.15)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <ClipboardList size={14} style={{ opacity: 0.5 }} /> {t('jobs_header_admin')}
                          </div>
                        </div>

                        {jobs.length === 0 ? (
                          <div style={{
                            color: 'var(--text-muted)', fontSize: '0.85rem', padding: '2rem 0',
                            textAlign: 'center', background: 'rgba(255,255,255,0.02)',
                            borderRadius: '12px', border: '1px dashed var(--glass-border)'
                          }}>
                            {t('no_jobs_found_admin')}
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1rem' }}>
                            {jobs.map(job => (
                              <div key={job.id} style={{
                                display: 'flex', flexDirection: 'column', gap: '1rem',
                                padding: '1.25rem', background: 'rgba(255,255,255,0.03)',
                                borderRadius: '14px', border: '1px solid var(--glass-border)',
                                transition: 'transform 0.2s, background 0.2s'
                              }} className="hover-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '1rem', letterSpacing: '-0.01em', marginBottom: '0.2rem' }}>{job.title}</div>
                                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '0.72rem', color: 'var(--accent-primary)', fontWeight: 700, textTransform: 'uppercase' }}>{job.type}</span>
                                      {job.salary_range && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>• {job.salary_range}</span>}
                                    </div>
                                  </div>
                                  <div style={{
                                    fontSize: '0.65rem', padding: '0.25rem 0.6rem', borderRadius: '6px',
                                    fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em',
                                    background: job.status === 'open' ? 'rgba(74,222,128,0.08)' : 'rgba(255,255,255,0.05)',
                                    color: job.status === 'open' ? '#4ade80' : 'var(--text-muted)',
                                    border: job.status === 'open' ? '1px solid rgba(74,222,128,0.15)' : '1px solid var(--glass-border)'
                                  }}>
                                    {job.status}
                                  </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.03)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--accent-primary)15', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)' }}>
                                      <Users size={14} />
                                    </div>
                                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-heading)' }}>{job.application_count || 0}</span>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>{t('applications_count_label')}</span>
                                  </div>

                                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                                    <button onClick={() => setJobModal(job)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.4rem', display: 'flex', alignItems: 'center' }} className="hover-bright"><Edit2 size={14} /></button>
                                    <button onClick={() => handleDeleteJob(job)} style={{ background: 'rgba(244,114,182,0.08)', border: '1px solid rgba(244,114,182,0.15)', borderRadius: '8px', color: '#f472b6', cursor: 'pointer', padding: '0.4rem', display: 'flex', alignItems: 'center' }} className="hover-bright"><Trash2 size={14} /></button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── APPLICATIONS TAB ──────────────────────────────────────────────── */}
      {activeTab === 'applications' && (
        <div className="animate-fade">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ padding: '0.75rem', borderRadius: '12px', background: 'var(--accent-primary)10', border: '1px solid var(--accent-primary)20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ClipboardList size={24} color="var(--accent-primary)" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.02em', marginBottom: '0.15rem' }}>{t('applications_tab_title')}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: applications.filter(a => a.status === 'pending').length > 0 ? '#facc15' : 'var(--text-muted)' }} />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>{applications.length} {t('applications_count_label')}</span>
                </div>
              </div>
            </div>

            {/* Segmented Filter Control */}
            <div style={{
              display: 'flex', padding: '0.25rem', background: 'rgba(0,0,0,0.2)',
              borderRadius: '12px', border: '1px solid var(--glass-border)',
              backdropFilter: 'blur(10px)'
            }}>
              {[
                { id: 'all', icon: Sparkles },
                { id: 'pending', icon: Activity },
                { id: 'shortlisted', icon: Sparkles },
                { id: 'hired', icon: CheckCircle },
                { id: 'rejected', icon: X }
              ].map(opt => {
                const isActive = appStatusFilter === opt.id;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setAppStatusFilter(opt.id)}
                    className="hover-card"
                    style={{
                      padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      background: isActive ? 'var(--accent-primary)' : 'transparent',
                      color: isActive ? 'white' : 'var(--text-muted)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isActive ? '0 4px 12px var(--accent-primary)40' : 'none'
                    }}
                  >
                    <Icon size={14} />
                    <span className="hide-mobile">{opt.id === 'all' ? t('all_label') : t('status_' + opt.id)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {appsLoading ? (
            <div style={{
              textAlign: 'center', color: 'var(--text-muted)', padding: '6rem',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'
            }}>
              <RefreshCw size={32} className="animate-spin" style={{ opacity: 0.3 }} />
              <p style={{ fontWeight: 600, letterSpacing: '0.05em' }}>{t('loading_label_admin')}</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="glass-card animate-fade" style={{
              padding: '6rem 2rem', textAlign: 'center', border: '1px dashed var(--glass-border)',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.01), transparent)'
            }}>
              <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 2rem' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'var(--accent-primary)10', borderRadius: '50%', filter: 'blur(20px)' }} />
                <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: '24px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                  <ClipboardList size={40} opacity={0.4} />
                </div>
              </div>
              <h4 style={{ fontWeight: 800, fontSize: '1.25rem', color: 'var(--text-heading)', marginBottom: '0.5rem' }}>{t('no_applications_yet')}</h4>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', maxWidth: '340px', margin: '0 auto' }}>
                There are no candidates in this category. New applications will appear here once submitted.
              </p>
            </div>
          ) : (
            <div className="glass-card animate-fade" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--glass-border)', background: 'rgba(0,0,0,0.15)' }}>
                      {[
                        t('candidate_label'),
                        t('position_title_label').replace(' *', ''),
                        t('tab_companies'),
                        t('score_label'),
                        t('job_status_label'),
                        t('actions_label')
                      ].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '1.25rem 1rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.08em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map(app => (
                      <tr key={app.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' }} className="hover-row">
                        <td style={{ padding: '1.25rem 1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, var(--bg-surface), var(--bg-card))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: 'var(--accent-primary)', border: '1px solid var(--glass-border)', fontSize: '0.9rem' }}>
                              {(app.full_name || app.username || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '0.92rem' }}>{app.full_name || app.username}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>{app.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '1.25rem 1rem' }}>
                          <div style={{ color: 'var(--text-body)', fontWeight: 700, fontSize: '0.88rem' }}>{app.job_title}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                            Applied {new Date(app.created_at).toLocaleDateString('el-GR')}
                          </div>
                        </td>
                        <td style={{ padding: '1.25rem 1rem' }}>
                          <span style={{ color: 'var(--accent-primary)', fontWeight: 800, fontSize: '0.82rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{app.company_name}</span>
                        </td>
                        <td style={{ padding: '1.25rem 1rem' }}>
                          {app.ai_score !== null && app.ai_score !== undefined ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ width: '32px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${app.ai_score}%`, background: app.ai_score >= 70 ? '#4ade80' : app.ai_score >= 40 ? '#facc15' : '#f472b6', boxShadow: `0 0 8px ${app.ai_score >= 70 ? '#4ade8050' : app.ai_score >= 40 ? '#facc1550' : '#f472b650'}` }} />
                              </div>
                              <span style={{ fontWeight: 800, fontSize: '0.8rem', color: app.ai_score >= 70 ? '#4ade80' : app.ai_score >= 40 ? '#facc15' : '#f472b6' }}>
                                {app.ai_score}%
                              </span>
                            </div>
                          ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td style={{ padding: '1.25rem 1rem' }}>
                          {/* Using the same global StatusBadge component if it exists, otherwise inline style */}
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                            padding: '0.35rem 0.75rem', borderRadius: '8px',
                            background: app.status === 'hired' ? 'rgba(74,222,128,0.1)' : app.status === 'pending' ? 'rgba(250,204,21,0.1)' : 'rgba(255,255,255,0.05)',
                            color: app.status === 'hired' ? '#4ade80' : app.status === 'pending' ? '#facc15' : 'var(--text-muted)',
                            fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase',
                            border: '1px solid currentColor', borderAlpha: 0.2
                          }}>
                            <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor' }} />
                            {t('status_' + app.status)}
                          </span>
                        </td>
                        <td style={{ padding: '1.25rem 1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <select
                              value={app.status}
                              disabled={updatingApp === app.id}
                              onChange={e => handleUpdateAppStatus(app.id, e.target.value)}
                              className="hover-bright"
                              style={{
                                padding: '0.4rem 0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--glass-border)',
                                borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.78rem', cursor: 'pointer',
                                outline: 'none', transition: 'all 0.2s'
                              }}
                            >
                              <option value="pending">{t('status_pending')}</option>
                              <option value="shortlisted">{t('status_shortlisted')}</option>
                              <option value="hired">{t('status_hired')}</option>
                              <option value="rejected">{t('status_rejected')}</option>
                            </select>

                            <button className="icon-btn-glass" title="View CV">
                              <ExternalLink size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {companyModal !== null && (
        <CompanyModal
          company={companyModal?.id ? companyModal : null}
          onClose={() => setCompanyModal(null)}
          onSave={handleCompanySave}
        />
      )}
      {jobModal !== null && (
        <JobModal
          job={jobModal?.id ? jobModal : null}
          companies={companies}
          onClose={() => setJobModal(null)}
          onSave={handleJobSave}
        />
      )}
      <InviteModal
        isOpen={inviteModal}
        form={inviteForm}
        onFormChange={setInviteForm}
        onSubmit={handleInviteUser}
        onClose={() => setInviteModal(false)}
        companies={companies}
      />
    </div>
  );
}

export default Admin;
